<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import axiosInstance from '@/services/interceptor.services';

const props = defineProps<{ storeId: string }>();

const { t, locale } = useI18n({ useScope: 'global' });

// ── Types ────────────────────────────────────────────────────────────────────
type DocType =
  'LEGAL_NOTICE' | 'SALES_TERMS' | 'COOKIE_POLICY' | 'PLATFORM_TERMS';
type DocLevel = 'PLATFORM' | 'ORGANIZATION' | 'COMMERCE';

interface LegalVersion {
  version: number;
  versionId: string;
  effectiveFrom: string | null;
  title: string;
  content: string;
  translationFallback: boolean;
}

interface LegalDoc {
  id: string;
  type: DocType;
  level: DocLevel;
  organizationId: string | null;
  restaurantId: string | null;
}

interface DocState {
  type: DocType;
  docId: string | null;
  draftVersionId: string | null;
  // Numéro de la version la plus récente (versions[0].version) — permet de
  // savoir si cette version est déjà publiée (=== publishedVersion, donc
  // immuable) ou encore un vrai brouillon modifiable.
  topVersionNumber: number | null;
  publishedVersion: number | null;
  publishedContent: string;
  publishedTitle: string;
  editorContent: string;
  editorTitle: string;
  loading: boolean;
  saving: boolean;
  saved: boolean;
  error: string | null;
  // Vrai quand l'erreur vient d'une version devenue PUBLISHED entre-temps :
  // affiche un bouton pour créer directement une nouvelle version brouillon.
  needsNewVersion: boolean;
  // Date/heure d'entrée en vigueur programmée (vide = publication immédiate).
  effectiveFrom: string;
}

// ── Doc definitions ──────────────────────────────────────────────────────────
const DOC_DEFS = computed<
  {
    type: DocType;
    owner: 'PLATFORM' | 'COMMERCE' | 'SHARED';
    editableByCommerce: boolean;
    readOnly: boolean;
  }[]
>(() => [
  {
    type: 'LEGAL_NOTICE',
    owner: 'COMMERCE',
    editableByCommerce: true,
    readOnly: false,
  },
  {
    type: 'SALES_TERMS',
    owner: 'COMMERCE',
    editableByCommerce: true,
    readOnly: false,
  },
  {
    type: 'COOKIE_POLICY',
    owner: 'SHARED',
    editableByCommerce: false,
    readOnly: true,
  },
  {
    type: 'PLATFORM_TERMS',
    owner: 'PLATFORM',
    editableByCommerce: false,
    readOnly: true,
  },
]);

// ── State per doc type ───────────────────────────────────────────────────────
const docStates = ref<Record<DocType, DocState>>({
  LEGAL_NOTICE: makeEmpty('LEGAL_NOTICE'),
  SALES_TERMS: makeEmpty('SALES_TERMS'),
  COOKIE_POLICY: makeEmpty('COOKIE_POLICY'),
  PLATFORM_TERMS: makeEmpty('PLATFORM_TERMS'),
});

function makeEmpty(type: DocType): DocState {
  return {
    type,
    docId: null,
    draftVersionId: null,
    topVersionNumber: null,
    publishedVersion: null,
    publishedContent: '',
    publishedTitle: '',
    editorContent: '',
    editorTitle: '',
    loading: false,
    saving: false,
    saved: false,
    error: null,
    needsNewVersion: false,
    effectiveFrom: '',
  };
}

// ── UI state ─────────────────────────────────────────────────────────────────
const editingType = ref<DocType | null>(null);
// 'view' = lecture seule (clic sur la ligne), 'edit' = éditeur (clic sur le crayon).
const viewMode = ref<'view' | 'edit'>('view');
const typeFilter = ref<DocType | ''>('');

const editingDef = computed(() =>
  editingType.value
    ? (DOC_DEFS.value.find((d) => d.type === editingType.value) ?? null)
    : null,
);

const editingState = computed(() =>
  editingType.value ? docStates.value[editingType.value] : null,
);

// ── Liste filtrable par type ───────────────────────────────────────────────────
const filteredDocDefs = computed(() =>
  typeFilter.value
    ? DOC_DEFS.value.filter((d) => d.type === typeFilter.value)
    : DOC_DEFS.value,
);

// ── Créer / continuer un document légal (tous les types — s'il existe déjà,
// on récupère directement son brouillon) ──────────────────────────────────────
const creatingNew = ref(false);
const newDocType = ref<DocType | ''>('');
const creatableTypes = computed(() =>
  DOC_DEFS.value.filter((d) => d.editableByCommerce),
);
function openCreateNew() {
  newDocType.value =
    creatableTypes.value.find((d) => !docStates.value[d.type].docId)?.type ??
    creatableTypes.value[0]?.type ??
    '';
  creatingNew.value = true;
}
function cancelCreateNew() {
  creatingNew.value = false;
  newDocType.value = '';
}
async function confirmCreateNew() {
  if (!newDocType.value) return;
  const type = newDocType.value;
  const s = docStates.value[type];
  // Type déjà créé pour ce commerce : récupère/crée directement son brouillon
  // au lieu de repartir d'un état vide.
  if (s.docId) {
    try {
      await getOrCreateDraft(type, s.docId, s);
    } catch {
      // en cas d'échec on ouvre quand même l'éditeur, l'utilisateur pourra réessayer
    }
  }
  editingType.value = type;
  viewMode.value = 'edit';
  creatingNew.value = false;
}

// ── Load all docs for this store ─────────────────────────────────────────────
async function loadAll() {
  // 1. Fetch published content for each type via public route
  for (const def of DOC_DEFS.value) {
    const s = docStates.value[def.type];
    s.loading = true;
    try {
      const res = await axiosInstance.get(`legal/${def.type}`, {
        params: { storeId: props.storeId },
        headers: {
          'accept-language': locale.value,
          'x-client-app': 'admin-back-office',
        },
      });
      const data = res.data?.data ?? res.data;
      s.publishedContent = data?.content ?? '';
      s.publishedTitle = data?.title ?? '';
      s.publishedVersion = data?.version ?? null;
      s.editorContent = s.publishedContent;
      s.editorTitle = s.publishedTitle || defaultTitle(def.type);
    } catch {
      s.editorTitle = defaultTitle(def.type);
    } finally {
      s.loading = false;
    }
  }

  // 2. Fetch admin doc list (filtre serveur storeId) to get ids + drafts
  try {
    const res = await axiosInstance.get('admin/legal-documents', {
      params: { storeId: props.storeId },
      headers: { 'x-client-app': 'admin-back-office' },
    });
    const list: LegalDoc[] = res.data?.data ?? res.data ?? [];
    for (const doc of list) {
      const s = docStates.value[doc.type];
      if (s) {
        s.docId = doc.id;
        await loadVersions(doc.type, doc.id);
      }
    }
  } catch {
    // silently ignore
  }
}

async function loadVersions(type: DocType, id: string) {
  const s = docStates.value[type];
  try {
    const res = await axiosInstance.get(
      `admin/legal-documents/${id}/versions`,
      {
        headers: { 'x-client-app': 'admin-back-office' },
      },
    );
    const versions: LegalVersion[] = res.data?.data ?? res.data ?? [];
    // La liste est triée de la plus récente à la plus ancienne (contrat API).
    const top = versions[0];
    if (top) {
      s.topVersionNumber = top.version;
      // La version la plus récente est un brouillon modifiable si et
      // seulement si elle n'est pas encore entrée en vigueur : effectiveFrom
      // === null signifie qu'elle n'a jamais été publiée ni programmée. Une
      // version publiée ou archivée a toujours un effectiveFrom renseigné et
      // est immuable côté API.
      const isDraft = top.effectiveFrom === null;
      s.draftVersionId = isDraft ? top.versionId : null;
      // Prefill from draft if no published content
      if (isDraft && !s.editorContent) {
        s.editorContent = top.content ?? '';
        s.editorTitle = top.title ?? s.editorTitle;
      }
    }
  } catch {
    // ignore
  }
}

// ── Create draft version : POST toujours envoyé, sans court-circuit ──────────
// Appelée uniquement quand on sait qu'il n'y a pas de draftVersionId connu
// (bouton "Créer le brouillon") — l'utilisateur veut explicitement déclencher
// cet appel, pas se faire servir un id en cache sans requête réseau.
async function createDraftVersion(
  type: DocType,
  id: string,
  s: DocState,
): Promise<string> {
  try {
    const res = await axiosInstance.post(
      `admin/legal-documents/${id}/versions`,
      { translations: buildTranslations(s) },
      { headers: { 'x-client-app': 'admin-back-office' } },
    );
    const created = res.data?.data ?? res.data;
    const vid = created?.versionId as string | undefined;
    if (vid) {
      s.draftVersionId = vid;
      s.topVersionNumber = created?.version ?? s.topVersionNumber;
      if (created?.content != null) s.editorContent = created.content;
      if (created?.title != null) s.editorTitle = created.title;
      return vid;
    }
    // Réponse inattendue : on refetch pour rester sûr.
    await loadVersions(type, id);
    if (s.draftVersionId) return s.draftVersionId;
    throw new Error('draft not found after creation');
  } catch (e: any) {
    if (e?.response?.status === 422) {
      // Un brouillon existe déjà côté serveur (course avec un autre onglet
      // par ex.) : on récupère son id plutôt que d'échouer.
      const existingId =
        e?.response?.data?.data?.versionId ??
        e?.response?.data?.data?.id ??
        e?.response?.data?.id;
      if (existingId) {
        s.draftVersionId = existingId;
        return existingId;
      }
      await loadVersions(type, id);
      if (s.draftVersionId) return s.draftVersionId;
    }
    throw e;
  }
}

// ── Get or create a draft version (always reads API first) ───────────────────
async function getOrCreateDraft(
  type: DocType,
  id: string,
  s: DocState,
): Promise<string> {
  // La version 1 en brouillon existe toujours dès la création du document
  // (POST /admin/legal-documents) : on la récupère via GET versions, on ne
  // POST /versions que si vraiment aucun brouillon n'existe encore (cas d'un
  // document déjà publié dont on veut une nouvelle version).
  await loadVersions(type, id);
  if (s.draftVersionId) return s.draftVersionId;
  return createDraftVersion(type, id, s);
}

// ── Étape 1 : créer le document (une fois) ────────────────────────────────────
async function createDoc(type: DocType): Promise<string> {
  const s = docStates.value[type];
  const res = await axiosInstance.post(
    'admin/legal-documents',
    { type, storeId: props.storeId, translations: buildTranslations(s) },
    { headers: { 'x-client-app': 'admin-back-office' } },
  );
  const id = (res.data?.data ?? res.data)?.id as string;
  s.docId = id;
  return id;
}

// ── Étape 2 : enregistrer le brouillon (créer le doc si besoin, sans publier) ─
async function saveDraft(type: DocType) {
  const s = docStates.value[type];
  s.saving = true;
  s.saved = false;
  s.error = null;

  try {
    const id = s.docId ?? (await createDoc(type));
    if (!id) throw new Error('no doc id');

    // Pas de brouillon connu : on appelle toujours Create draft version
    // (translations omis → reprend le contenu en vigueur), sans jamais se
    // faire servir un id en cache sans requête réseau — l'utilisateur a
    // cliqué explicitement sur "Créer le brouillon". S'il y a déjà un
    // brouillon connu, on va directement à l'update (PUT).
    const hadDraft = !!s.draftVersionId;
    let versionId = hadDraft
      ? s.draftVersionId!
      : await createDraftVersion(type, id, s);
    if (!versionId) throw new Error('no version id');

    if (hadDraft) {
      try {
        await axiosInstance.put(
          `admin/legal-documents/${id}/versions/${versionId}`,
          { translations: buildTranslations(s) },
          { headers: { 'x-client-app': 'admin-back-office' } },
        );
      } catch (e: any) {
        // Le brouillon en cache a été publié entre-temps (hors de cet écran) :
        // on force une nouvelle version puis on retente une seule fois.
        if (e?.response?.data?.error === 'LegalVersionNotEditableException') {
          s.draftVersionId = null;
          versionId = await getOrCreateDraft(type, id, s);
          if (!versionId) throw e;
          await axiosInstance.put(
            `admin/legal-documents/${id}/versions/${versionId}`,
            { translations: buildTranslations(s) },
            { headers: { 'x-client-app': 'admin-back-office' } },
          );
        } else {
          throw e;
        }
      }
    }

    // On connaît déjà le bon versionId (retourné par getOrCreateDraft/PUT) :
    // pas de refetch ici, pour éviter qu'un délai de cohérence du GET écrase
    // s.draftVersionId avec un état serveur pas encore à jour.
    s.draftVersionId = versionId;
    s.saved = true;
    setTimeout(() => {
      s.saved = false;
    }, 2500);
  } catch (e: any) {
    if (e?.response?.data?.error === 'LegalVersionNotEditableException') {
      s.draftVersionId = null;
      s.needsNewVersion = true;
      s.error = t('legal.versionAlreadyPublished');
    } else {
      const msg = e?.response?.data?.message;
      s.error = Array.isArray(msg)
        ? msg.join(' ')
        : (msg ?? t('legal.publishError'));
    }
  } finally {
    s.saving = false;
  }
}

// ── Étape 3 : publier le brouillon existant ───────────────────────────────────
async function publishDraft(type: DocType, effectiveFrom?: string) {
  const s = docStates.value[type];
  if (!s.docId || !s.draftVersionId) return;
  s.saving = true;
  s.saved = false;
  s.error = null;
  s.needsNewVersion = false;

  try {
    await axiosInstance.post(
      `admin/legal-documents/${s.docId}/versions/${s.draftVersionId}/publish`,
      effectiveFrom
        ? { effectiveFrom: new Date(effectiveFrom).toISOString() }
        : {},
      { headers: { 'x-client-app': 'admin-back-office' } },
    );

    s.publishedVersion = (s.publishedVersion ?? 0) + 1;
    s.publishedContent = s.editorContent;
    s.publishedTitle = s.editorTitle;
    // Resync état draft depuis l'API — ne pas deviner
    await loadVersions(type, s.docId);
    s.saved = true;
    setTimeout(() => {
      s.saved = false;
    }, 2500);
  } catch (e: any) {
    if (e?.response?.data?.error === 'LegalVersionNotEditableException') {
      // Le brouillon en cache a déjà été publié entre-temps (hors de cet
      // écran) : on le signale et on propose de créer une nouvelle version.
      s.draftVersionId = null;
      s.needsNewVersion = true;
      s.error = t('legal.versionAlreadyPublished');
    } else {
      const msg = e?.response?.data?.message;
      s.error = Array.isArray(msg)
        ? msg.join(' ')
        : (msg ?? t('legal.publishError'));
    }
  } finally {
    s.saving = false;
  }
}

function buildTranslations(s: DocState) {
  return [
    { languageCode: 'fr', title: s.editorTitle, content: s.editorContent },
    { languageCode: 'en', title: s.editorTitle, content: s.editorContent },
    { languageCode: 'nl', title: s.editorTitle, content: s.editorContent },
  ];
}

function defaultTitle(type: DocType): string {
  return t(`legal.types.${type}`);
}

// ── Markdown preview ──────────────────────────────────────────────────────────
function renderMarkdown(md: string): string {
  if (!md) return '';
  return md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<oli>$1</oli>')
    .replace(/^---$/gm, '<hr>')
    .split(/\n{2,}/)
    .map((block) => {
      block = block.trim();
      if (!block) return '';
      if (/^<(h[1-6]|hr|ul|ol|li|oli)/.test(block)) return block;
      if (block.includes('<li>')) return '<ul>' + block + '</ul>';
      if (block.includes('<oli>'))
        return (
          '<ol>' +
          block.replace(/<\/?oli>/g, (m) =>
            m === '<oli>' ? '<li>' : '</li>',
          ) +
          '</ol>'
        );
      return '<p>' + block.replace(/\n/g, '<br>') + '</p>';
    })
    .join('\n');
}

async function openEditor(type: DocType, mode: 'view' | 'edit' = 'view') {
  const def = DOC_DEFS.value.find((d) => d.type === type);
  const effectiveMode = def?.readOnly ? 'view' : mode;
  editingType.value = type;
  viewMode.value = effectiveMode;
  // Au clic Edit : garantit qu'un brouillon existe (le crée si besoin, à
  // partir de la version en vigueur) avant d'entrer en édition.
  const s = docStates.value[type];
  if (effectiveMode === 'edit' && s.docId) {
    try {
      await getOrCreateDraft(type, s.docId, s);
    } catch {
      // édition possible malgré tout, saveDraft réessaiera
    }
  }
}

async function switchToEdit() {
  if (!editingType.value) return;
  const def = DOC_DEFS.value.find((d) => d.type === editingType.value);
  if (def?.readOnly) return;
  viewMode.value = 'edit';
  if (editingType.value) {
    const s = docStates.value[editingType.value];
    if (s.docId) {
      try {
        await getOrCreateDraft(editingType.value, s.docId, s);
      } catch {
        // édition possible malgré tout, saveDraft réessaiera
      }
    }
  }
}

function closeEditor() {
  editingType.value = null;
  viewMode.value = 'view';
}

// ── Owner badge ───────────────────────────────────────────────────────────────
function ownerLabel(owner: string) {
  if (owner === 'PLATFORM') return t('legal.ownerPlatform');
  if (owner === 'COMMERCE') return t('legal.ownerCommerce');
  return t('legal.ownerShared');
}

function ownerClass(owner: string) {
  if (owner === 'PLATFORM') return 'lgl__badge--platform';
  if (owner === 'COMMERCE') return 'lgl__badge--commerce';
  return 'lgl__badge--shared';
}

onMounted(loadAll);
watch(() => props.storeId, loadAll);
</script>

<template>
  <div class="lgl">
    <!-- ── List view ────────────────────────────────────────────────────────── -->
    <template v-if="!editingType">
      <div class="lgl__header">
        <div>
          <h2 class="lgl__title">{{ t('legal.title') }}</h2>
          <p class="lgl__sub">{{ t('legal.subtitle') }}</p>
        </div>
        <div v-if="!creatingNew" class="lgl__header-actions">
          <div class="lgl__filter-wrap">
            <label class="lgl__field-label">{{ t('legal.filterType') }}</label>
            <select
              v-model="typeFilter"
              class="lgl__field-input lgl__filter-select"
            >
              <option value="">{{ t('legal.allTypes') }}</option>
              <option v-for="def in DOC_DEFS" :key="def.type" :value="def.type">
                {{ t(`legal.types.${def.type}`) }}
              </option>
            </select>
          </div>
          <button
            class="lgl__btn lgl__btn--primary lgl__create-btn"
            @click="openCreateNew"
          >
            <i class="pi pi-plus" /> {{ t('legal.createNew') }}
          </button>
        </div>
      </div>

      <!-- Panneau : choix du type à créer -->
      <div v-if="creatingNew" class="lgl__create-panel">
        <p class="lgl__field-label">{{ t('legal.chooseType') }}</p>
        <div class="lgl__create-type-list">
          <label
            v-for="def in creatableTypes"
            :key="def.type"
            class="lgl__create-type-opt"
            :class="{ 'lgl__create-type-opt--sel': newDocType === def.type }"
          >
            <input
              type="radio"
              v-model="newDocType"
              :value="def.type"
              style="display: none"
            />
            <span class="lgl__create-type-name">
              {{ t(`legal.types.${def.type}`) }}
              <span
                v-if="docStates[def.type].docId"
                class="lgl__badge lgl__badge--version"
              >
                {{ t('legal.alreadyCreated') }}
              </span>
            </span>
            <span class="lgl__create-type-desc">{{
              t(`legal.descs.${def.type}`)
            }}</span>
          </label>
        </div>
        <div class="lgl__create-panel-actions">
          <button class="lgl__btn lgl__btn--ghost" @click="cancelCreateNew">
            {{ t('legal.cancel') }}
          </button>
          <button
            class="lgl__btn lgl__btn--primary"
            :disabled="!newDocType"
            @click="confirmCreateNew"
          >
            <i class="pi pi-arrow-right" /> {{ t('legal.continue') }}
          </button>
        </div>
      </div>

      <template v-if="!creatingNew">
        <!-- Legend -->
        <div class="lgl__legend">
          <span class="lgl__badge lgl__badge--platform">{{
            t('legal.ownerPlatform')
          }}</span>
          <span class="lgl__legend-sep">{{
            t('legal.legendPlatformDesc')
          }}</span>
          <span class="lgl__badge lgl__badge--commerce">{{
            t('legal.ownerCommerce')
          }}</span>
          <span class="lgl__legend-sep">{{
            t('legal.legendCommerceDesc')
          }}</span>
          <span class="lgl__badge lgl__badge--shared">{{
            t('legal.ownerShared')
          }}</span>
          <span class="lgl__legend-sep">{{ t('legal.legendSharedDesc') }}</span>
        </div>

        <!-- Doc rows -->
        <div v-if="!filteredDocDefs.length" class="lgl__empty">
          <i class="pi pi-file-o" />
          <p>{{ t('legal.noResults') }}</p>
        </div>
        <div v-else class="lgl__list">
          <div
            v-for="def in filteredDocDefs"
            :key="def.type"
            class="lgl__row lgl__row--clickable"
            @click="openEditor(def.type, 'view')"
          >
            <div class="lgl__row-main">
              <div class="lgl__row-meta">
                <span class="lgl__badge" :class="ownerClass(def.owner)">
                  {{ ownerLabel(def.owner) }}
                </span>
                <span
                  v-if="
                    docStates[def.type].docId &&
                    docStates[def.type].publishedVersion
                  "
                  class="lgl__badge lgl__badge--version"
                >
                  v{{ docStates[def.type].publishedVersion }}
                </span>
                <span
                  v-else-if="
                    !docStates[def.type].docId &&
                    docStates[def.type].publishedContent
                  "
                  class="lgl__badge lgl__badge--nextep"
                >
                  {{ t('legal.inheritedFromPlatform') }}
                </span>
                <span
                  v-if="!def.readOnly && !docStates[def.type].publishedContent"
                  class="lgl__badge lgl__badge--missing"
                >
                  {{ t('legal.statusMissing') }}
                </span>
                <span
                  v-else-if="
                    !def.readOnly && docStates[def.type].publishedContent
                  "
                  class="lgl__badge lgl__badge--ok"
                >
                  {{ t('legal.statusPublished') }}
                </span>
                <span v-else class="lgl__badge lgl__badge--nextep">
                  {{ t('legal.statusNextep') }}
                </span>
              </div>

              <h3 class="lgl__row-name">{{ t(`legal.types.${def.type}`) }}</h3>
              <p class="lgl__row-desc">{{ t(`legal.descs.${def.type}`) }}</p>
            </div>

            <div class="lgl__row-actions">
              <div v-if="docStates[def.type].loading" class="lgl__row-loading">
                <i class="pi pi-spin pi-spinner" />
              </div>
              <template v-else>
                <button
                  class="lgl__btn lgl__btn--ghost"
                  @click.stop="openEditor(def.type, 'view')"
                >
                  <i class="pi pi-eye" />
                  {{ t('legal.view') }}
                </button>
                <button
                  v-if="!def.readOnly"
                  class="lgl__icon-btn"
                  :title="t('legal.edit')"
                  @click.stop="openEditor(def.type, 'edit')"
                >
                  <i class="pi pi-pencil" />
                </button>
              </template>
            </div>
          </div>
        </div>
      </template>
    </template>

    <!-- ── Editor view ───────────────────────────────────────────────────────── -->
    <template v-else-if="editingState && editingDef">
      <div class="lgl__editor-header">
        <button class="lgl__back" @click="closeEditor()">
          <i class="pi pi-arrow-left" />
          {{ t('legal.backToList') }}
        </button>

        <div class="lgl__editor-title-row">
          <div>
            <div class="lgl__editor-eyebrow">
              <span class="lgl__badge" :class="ownerClass(editingDef.owner)">
                {{ ownerLabel(editingDef.owner) }}
              </span>
              <span
                v-if="editingState.docId && editingState.publishedVersion"
                class="lgl__badge lgl__badge--version"
              >
                v{{ editingState.publishedVersion }}
              </span>
              <span
                v-else-if="!editingState.docId && editingState.publishedContent"
                class="lgl__badge lgl__badge--nextep"
              >
                {{ t('legal.inheritedFromPlatform') }}
              </span>
            </div>
            <h2 class="lgl__editor-name">
              {{ t(`legal.types.${editingDef.type}`) }}
            </h2>
            <p class="lgl__editor-sub">
              {{ t(`legal.descs.${editingDef.type}`) }}
            </p>
          </div>

          <div
            v-if="viewMode === 'view' && !editingDef.readOnly"
            class="lgl__editor-actions"
          >
            <button class="lgl__btn lgl__btn--primary" @click="switchToEdit">
              <i class="pi pi-pencil" /> {{ t('legal.edit') }}
            </button>
          </div>

          <div
            v-else-if="viewMode === 'edit' && !editingDef.readOnly"
            class="lgl__editor-actions"
          >
            <button
              class="lgl__btn lgl__btn--ghost"
              :disabled="editingState.saving"
              @click="editingState.editorContent = ''"
            >
              {{ t('legal.reset') }}
            </button>

            <!-- Étape : enregistrer le contenu actuel dans la version courante
                 (saveDraft crée/récupère le brouillon lui-même si besoin) -->
            <button
              class="lgl__btn lgl__btn--primary"
              :disabled="editingState.saving"
              @click="saveDraft(editingDef.type)"
            >
              <i v-if="editingState.saving" class="pi pi-spin pi-spinner" />
              <i v-else-if="editingState.saved" class="pi pi-check" />
              <i v-else class="pi pi-save" />
              <span>
                {{
                  editingState.saving
                    ? t('terms.saving')
                    : editingState.docId && editingState.draftVersionId
                      ? t('legal.updateVersion')
                      : t('legal.createDraft')
                }}
              </span>
            </button>

            <!-- Étape : publier cette version (désactivé tant qu'aucun
                 brouillon modifiable n'existe — une version déjà publiée est
                 immuable, il faut d'abord recréer un brouillon) -->
            <div v-if="editingState.docId" class="lgl__publish-wrap">
              <div class="lgl__publish-date-field">
                <input
                  v-model="editingState.effectiveFrom"
                  type="datetime-local"
                  class="lgl__field-input lgl__publish-date"
                />
                <span class="lgl__publish-hint">{{
                  t('legal.effectiveFromHint')
                }}</span>
              </div>
              <button
                class="lgl__btn lgl__btn--publish"
                :disabled="editingState.saving || !editingState.draftVersionId"
                @click="
                  publishDraft(editingDef.type, editingState.effectiveFrom)
                "
              >
                <i v-if="editingState.saving" class="pi pi-spin pi-spinner" />
                <i v-else class="pi pi-send" />
                {{
                  editingState.effectiveFrom
                    ? t('legal.publishScheduled')
                    : t('legal.publishVersion')
                }}
              </button>
            </div>
          </div>
        </div>

        <!-- Indicateur d'étapes -->
        <div v-if="viewMode === 'edit'" class="lgl__steps">
          <span
            class="lgl__step"
            :class="{ 'lgl__step--done': !!editingState.docId }"
          >
            <span class="lgl__step-dot">1</span>
            {{ t('legal.stepCreate') }}
          </span>
          <i class="pi pi-angle-right lgl__step-sep" />
          <span
            class="lgl__step"
            :class="{ 'lgl__step--done': !!editingState.draftVersionId }"
          >
            <span class="lgl__step-dot">2</span>
            {{ t('legal.stepDraft') }}
          </span>
          <i class="pi pi-angle-right lgl__step-sep" />
          <span
            class="lgl__step"
            :class="{ 'lgl__step--done': !!editingState.publishedVersion }"
          >
            <span class="lgl__step-dot">3</span>
            {{ t('legal.stepPublish') }}
          </span>
        </div>

        <!-- Error -->
        <div v-if="editingState.error" class="lgl__error">
          <i class="pi pi-exclamation-triangle" />
          <span>{{ editingState.error }}</span>
          <button
            class="lgl__dismiss-btn"
            :title="t('legal.dismiss')"
            @click="
              editingState.error = null;
              editingState.needsNewVersion = false;
            "
          >
            <i class="pi pi-times" />
          </button>
        </div>
        <div v-if="editingState.saved" class="lgl__success">
          <i class="pi pi-check-circle" />
          {{ t('legal.saved') }}
        </div>
      </div>

      <!-- Title field -->
      <div v-if="viewMode === 'edit'" class="lgl__title-field">
        <label class="lgl__field-label">{{ t('terms.titleField') }}</label>
        <input
          v-model="editingState.editorTitle"
          class="lgl__field-input"
          type="text"
          :placeholder="t(`legal.types.${editingDef.type}`)"
        />
      </div>
      <div v-else class="lgl__title-field">
        <label class="lgl__field-label">{{ t('terms.titleField') }}</label>
        <p class="lgl__title-readonly">
          {{ editingState.editorTitle || t(`legal.types.${editingDef.type}`) }}
        </p>
      </div>

      <!-- Split panes -->
      <div class="lgl__split">
        <!-- Editor -->
        <div class="lgl__pane lgl__pane--editor">
          <div class="lgl__pane-head">
            <i class="pi pi-file-edit lgl__pane-ico" />
            <span>{{ t('terms.editor') }}</span>
            <span class="lgl__pane-badge">Markdown</span>
          </div>
          <textarea
            v-if="viewMode === 'edit'"
            v-model="editingState.editorContent"
            class="lgl__textarea"
            :placeholder="t('terms.editorPlaceholder')"
          />
          <div v-else class="lgl__textarea lgl__textarea--readonly">
            {{ editingState.editorContent || t('legal.noContent') }}
          </div>
        </div>

        <!-- Divider -->
        <div class="lgl__divider" />

        <!-- Preview -->
        <div class="lgl__pane lgl__pane--preview">
          <div class="lgl__pane-head">
            <i class="pi pi-eye lgl__pane-ico" />
            <span>{{ t('terms.preview') }}</span>
            <span class="lgl__pane-badge lgl__pane-badge--live">Live</span>
          </div>
          <div
            class="lgl__preview"
            v-html="renderMarkdown(editingState.editorContent)"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.lgl {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem 0;
}

/* ── Header ───────────────────────────────────────────────────────────────── */
.lgl__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}
.lgl__title {
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  letter-spacing: -0.02em;
}
.lgl__sub {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
}
.lgl__filter-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 12rem;
}
.lgl__filter-select {
  cursor: pointer;
}
.lgl__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  color: var(--nxt-on-surface-muted, #94a3b8);
}
.lgl__empty i {
  font-size: 2rem;
  opacity: 0.4;
}

/* ── Créer un nouveau document ────────────────────────────────────────────── */
.lgl__header-actions {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
}
.lgl__create-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.lgl__create-panel {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1.25rem;
  border-radius: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.lgl__create-type-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.lgl__create-type-opt {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  cursor: pointer;
  transition: border-color 0.15s;
}
.lgl__create-type-opt--sel {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.08);
}
.lgl__create-type-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.lgl__create-type-desc {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted, #94a3b8);
}
.lgl__create-panel-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

/* ── Legend ───────────────────────────────────────────────────────────────── */
.lgl__legend {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}
.lgl__legend-sep {
  opacity: 0.6;
}

/* ── Badges ───────────────────────────────────────────────────────────────── */
.lgl__badge {
  display: inline-flex;
  align-items: center;
  height: 1.35rem;
  padding: 0 0.5rem;
  border-radius: 9999px;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  white-space: nowrap;
}
.lgl__badge--platform {
  background: rgba(99, 102, 241, 0.12);
  color: #6366f1;
}
.lgl__badge--commerce {
  background: rgba(245, 158, 11, 0.12);
  color: #d97706;
}
.lgl__badge--shared {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}
.lgl__badge--version {
  background: rgba(0, 0, 0, 0.07);
  color: var(--nxt-on-surface-variant);
}
.lgl__badge--missing {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}
.lgl__badge--ok {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}
.lgl__badge--nextep {
  background: rgba(99, 102, 241, 0.08);
  color: #6366f1;
}

/* ── Row list ─────────────────────────────────────────────────────────────── */
.lgl__list {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.lgl__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.125rem 1.25rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.06));
}
.lgl__row:last-child {
  border-bottom: none;
}
.lgl__row--clickable {
  cursor: pointer;
  transition: background 0.15s ease;
}
.lgl__row--clickable:hover {
  background: rgba(0, 0, 0, 0.02);
}
[data-theme='dark'] .lgl__row--clickable:hover {
  background: rgba(255, 255, 255, 0.03);
}
.lgl__row-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.lgl__row-meta {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}
.lgl__row-name {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.lgl__row-desc {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.5;
}
.lgl__row-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}
.lgl__row-loading {
  color: var(--nxt-on-surface-variant);
  opacity: 0.5;
}

/* ── Buttons ──────────────────────────────────────────────────────────────── */
.lgl__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 2rem;
  padding: 0 0.875rem;
  border-radius: 0.45rem;
  border: 1.5px solid transparent;
  font-size: 0.8rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition:
    opacity 0.15s,
    background 0.15s;
  white-space: nowrap;
}
.lgl__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.lgl__btn--ghost {
  background: transparent;
  color: var(--nxt-on-surface-variant);
  border-color: var(--nxt-outline-variant, #e0e0e0);
}
.lgl__btn--ghost:hover:not(:disabled) {
  background: var(--nxt-surface-low);
}
.lgl__btn--primary {
  background: var(--nxt-on-surface, #111);
  color: #fff;
  border-color: var(--nxt-on-surface, #111);
}
.lgl__btn--primary:hover:not(:disabled) {
  opacity: 0.88;
}
[data-theme='dark'] .lgl__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}

/* ── Editor header ────────────────────────────────────────────────────────── */
.lgl__editor-header {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex-shrink: 0;
}
.lgl__back {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: none;
  border: none;
  color: var(--nxt-primary);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  padding: 0;
  width: fit-content;
}
.lgl__back:hover {
  text-decoration: underline;
}
.lgl__editor-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.lgl__editor-eyebrow {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.3rem;
}
.lgl__editor-name {
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  letter-spacing: -0.02em;
}
.lgl__editor-sub {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
}
.lgl__editor-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

/* Error */
.lgl__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-radius: 0.625rem;
  background: rgba(239, 68, 68, 0.09);
  color: #dc2626;
  font-size: 0.8125rem;
  font-weight: 600;
}
.lgl__error span {
  flex: 1;
}
.lgl__error-action {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 1.75rem;
  padding: 0 0.625rem;
  border: 1.5px solid currentColor;
  border-radius: 0.4rem;
  background: transparent;
  color: #dc2626;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
}
.lgl__error-action:hover:not(:disabled) {
  background: rgba(220, 38, 38, 0.08);
}
.lgl__error-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.lgl__dismiss-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: inherit;
  cursor: pointer;
  flex-shrink: 0;
  opacity: 0.7;
}
.lgl__dismiss-btn:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.06);
}
.lgl__success {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-radius: 0.625rem;
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  font-size: 0.8125rem;
  font-weight: 600;
}
.lgl__btn--publish {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}
.lgl__btn--publish:hover:not(:disabled) {
  opacity: 0.88;
}
.lgl__publish-wrap {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.lgl__publish-date-field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.lgl__publish-date {
  width: 12rem;
}
.lgl__publish-hint {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-muted, #94a3b8);
}

/* Indicateur d'étapes */
.lgl__steps {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.lgl__step {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-on-surface-muted, #94a3b8);
}
.lgl__step-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background: var(--nxt-surface-mid, #e5e7eb);
  color: var(--nxt-on-surface-muted, #94a3b8);
  font-size: 0.6875rem;
  font-weight: 700;
}
.lgl__step--done {
  color: var(--nxt-on-surface, #111);
}
.lgl__step--done .lgl__step-dot {
  background: #ffd60a;
  color: #0b1320;
}
.lgl__step-sep {
  font-size: 0.625rem;
  color: var(--nxt-outline-variant, #d1d5db);
}

/* Title field */
.lgl__title-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  flex-shrink: 0;
}
.lgl__field-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-variant);
}
.lgl__field-input {
  height: 2.25rem;
  padding: 0 0.75rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-family: inherit;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface);
  outline: none;
  transition: border-color 0.15s;
}
.lgl__field-input:focus {
  border-color: var(--nxt-primary);
}
.lgl__title-readonly {
  margin: 0;
  padding: 0.4rem 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.lgl__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: border-color 0.15s;
}
.lgl__icon-btn:hover {
  border-color: #ffd60a;
  color: var(--nxt-on-surface);
}

/* ── Split editor ─────────────────────────────────────────────────────────── */
.lgl__split {
  flex: 1;
  min-height: 420px;
  display: flex;
  gap: 0;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.lgl__pane {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.lgl__pane-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.02));
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-variant);
  flex-shrink: 0;
}
.lgl__pane-ico {
  font-size: 0.8rem;
  opacity: 0.6;
}
.lgl__pane-badge {
  margin-left: auto;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.15rem 0.45rem;
  border-radius: 9999px;
  background: var(--nxt-outline-variant, #e0e0e0);
  color: var(--nxt-on-surface-variant);
}
.lgl__pane-badge--live {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}
.lgl__divider {
  width: 1px;
  background: var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  flex-shrink: 0;
}
.lgl__textarea {
  flex: 1;
  min-height: 0;
  display: block;
  width: 100%;
  padding: 1rem 1.25rem;
  border: none;
  outline: none;
  resize: none;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: 'Menlo', 'Consolas', 'SF Mono', monospace;
  line-height: 1.8;
  box-sizing: border-box;
  overflow-y: auto;
}
.lgl__textarea--readonly {
  white-space: pre-wrap;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.7;
}
.lgl__textarea::placeholder {
  color: var(--nxt-on-surface-variant);
  opacity: 0.4;
}
.lgl__preview {
  flex: 1;
  min-height: 0;
  padding: 1rem 1.5rem;
  overflow-y: auto;
  font-size: 0.9rem;
  line-height: 1.75;
  color: var(--nxt-on-surface);
}
.lgl__preview :deep(h1) {
  font-size: 1.35rem;
  font-weight: 800;
  margin: 0 0 0.75rem;
  color: var(--nxt-on-surface);
}
.lgl__preview :deep(h2) {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 1.25rem 0 0.5rem;
  color: var(--nxt-on-surface);
  padding-bottom: 0.375rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
}
.lgl__preview :deep(h3) {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 1rem 0 0.4rem;
  color: var(--nxt-on-surface);
}
.lgl__preview :deep(p) {
  margin: 0 0 0.75rem;
  color: var(--nxt-on-surface-variant, #555);
}
.lgl__preview :deep(strong) {
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.lgl__preview :deep(em) {
  font-style: italic;
}
.lgl__preview :deep(code) {
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 0.8rem;
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.05));
  padding: 0.1em 0.35em;
  border-radius: 0.25rem;
  color: var(--nxt-primary);
}
.lgl__preview :deep(ul) {
  margin: 0 0 0.75rem 1.1rem;
  padding: 0;
  list-style: disc;
}
.lgl__preview :deep(ol) {
  margin: 0 0 0.75rem 1.1rem;
  padding: 0;
  list-style: decimal;
}
.lgl__preview :deep(li) {
  margin-bottom: 0.2rem;
  color: var(--nxt-on-surface-variant);
}
.lgl__preview :deep(hr) {
  border: none;
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  margin: 1.25rem 0;
}

/* No content */
.lgl__no-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface-variant);
  opacity: 0.4;
  font-size: 0.875rem;
}

/* Dark mode */
[data-theme='dark'] .lgl__pane-head {
  background: rgba(255, 255, 255, 0.025);
}
[data-theme='dark'] .lgl__textarea {
  background: transparent;
}
[data-theme='dark'] .lgl__badge--version {
  background: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .lgl__pane-badge {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .lgl__field-input {
  background: transparent;
}
[data-theme='dark'] .lgl__list,
[data-theme='dark'] .lgl__split {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
</style>
